/**
 * Regroup the language versions of a same point, and order the list of a project.
 *
 * Grouping rests on one signal only: `sourceAudioAIId`, which the translation worker writes on
 * every audio it creates, pointing at the audio the text came from (see `addItemToProject` on the
 * back end). Following it up to the root of the chain gives the point, whatever the depth — a
 * translation of a translation, or a mix derived from a translation, all land on the same root.
 *
 * Nothing is inferred from the titles. Audios translated before that column existed simply stay on
 * their own row; sorting the list by title puts them next to each other anyway, since the worker
 * names a translation `"<source title> - <Language>"`, which sorts right after its source.
 */

export interface GroupableAudio {
    id: string;
    name?: string | null;
    language?: string | null;
    sourceAudioAIId?: string | null;
    position?: number;
    createdAt?: string;
}

/** How the rows of a project are ordered. `position` is the order saved on the project. */
export type SortMode = "position" | "title" | "createdAt";

export interface AudioLanguageGroup<T extends GroupableAudio> {
    /** Stable across renders: the id of the root audio of the group. */
    key: string;
    /** Title of the point, i.e. the title of the version the others were translated from. */
    title: string;
    items: T[];
}

/** Iterative union-find; the groups are small but a lineage chain can be of any length. */
class UnionFind {
    private parent = new Map<string, string>();

    find(id: string): string {
        let root = id;
        while (this.parent.has(root)) {
            const next = this.parent.get(root) as string;
            if (next === root) break;
            root = next;
        }
        // Path compression, so repeated lookups stay flat.
        let cursor = id;
        while (this.parent.has(cursor) && this.parent.get(cursor) !== root) {
            const next = this.parent.get(cursor) as string;
            this.parent.set(cursor, root);
            cursor = next;
        }
        return root;
    }

    union(a: string, b: string): void {
        const rootA = this.find(a);
        const rootB = this.find(b);
        if (rootA !== rootB) this.parent.set(rootB, rootA);
    }
}

const positionOf = (audio: GroupableAudio): number =>
    typeof audio.position === "number" ? audio.position : Number.MAX_SAFE_INTEGER;

const titleOf = (audio: GroupableAudio): string => (audio.name || "").trim();

/**
 * Numeric-aware, so "Salle 2" comes before "Salle 10", and accent-tolerant, so a title is never
 * pushed to the end of the list because it starts with an accented letter.
 */
const compareTitles = (a: string, b: string): number =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });

/** Sorts a copy, so the caller keeps the list it was handed. */
export function sortAudios<T extends GroupableAudio>(audios: T[], mode: SortMode): T[] {
    const sorted = [...audios];

    if (mode === "title") {
        // A translation is named "<source title> - <Language>", so it sorts right after its source
        // without anything having to be guessed from that title.
        return sorted.sort((a, b) => compareTitles(titleOf(a), titleOf(b)) || positionOf(a) - positionOf(b));
    }

    if (mode === "createdAt") {
        // Newest first: this order answers "what did I just generate".
        return sorted.sort(
            (a, b) => (b.createdAt || "").localeCompare(a.createdAt || "") || positionOf(a) - positionOf(b),
        );
    }

    return sorted.sort((a, b) => positionOf(a) - positionOf(b));
}

export function groupAudiosByPoint<T extends GroupableAudio>(audios: T[], mode: SortMode): AudioLanguageGroup<T>[] {
    const unionFind = new UnionFind();

    // A source that left the project — deleted, or moved elsewhere — is still a valid pivot: two
    // translations of it remain versions of the same point.
    audios.forEach((audio) => {
        if (!audio.sourceAudioAIId || audio.sourceAudioAIId === audio.id) return;
        unionFind.union(audio.sourceAudioAIId, audio.id);
    });

    const grouped = new Map<string, T[]>();
    audios.forEach((audio) => {
        const root = unionFind.find(audio.id);
        const bucket = grouped.get(root);
        if (bucket) bucket.push(audio);
        else grouped.set(root, [audio]);
    });

    const groups: AudioLanguageGroup<T>[] = [];
    grouped.forEach((items) => {
        // The version the others were translated from leads the group; the rest keeps the order of
        // the list, which the caller has already sorted.
        const isDerived = (audio: T): number =>
            audio.sourceAudioAIId && items.some((item) => item.id === audio.sourceAudioAIId) ? 1 : 0;
        const sorted = [...items].sort((a, b) => isDerived(a) - isDerived(b));

        groups.push({
            key: sorted[0].id,
            title: titleOf(sorted[0]),
            items: sorted,
        });
    });

    // Groups take the rank of their leading version, so they follow the sort chosen for the list.
    if (mode === "title") {
        return groups.sort((a, b) => compareTitles(a.title, b.title));
    }
    if (mode === "createdAt") {
        return groups.sort((a, b) => (b.items[0].createdAt || "").localeCompare(a.items[0].createdAt || ""));
    }
    return groups.sort((a, b) => positionOf(a.items[0]) - positionOf(b.items[0]));
}
